import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Row, 
    Col, 
    Pagination, 
    Empty, 
    Spin, 
    Typography, 
    Tag, 
    Button, 
    Space, 
    Tooltip,
    Badge,
    Divider
} from 'antd';
import { 
    StarOutlined, 
    EnvironmentOutlined, 
    ClockCircleOutlined,
    HeartOutlined,
    HeartFilled,
    ShareAltOutlined,
    EyeOutlined,
    ShoppingCartOutlined
} from '@ant-design/icons';
import { useSearch } from '../../contexts/SearchContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { useCart } from '../../contexts/CartContext';
import WishlistButton from '../UI/WishlistButton';
import SocialShare from '../UI/SocialShare';
import LoadingSkeleton from '../UI/LoadingSkeleton';
import { PLACEHOLDER_IMG, safeImageOnError, formatCurrency } from '../../utils/ui';
import MenuCard from '../UI/MenuCard';
import FoodCard from '../UI/FoodCard';
import VendorCard from '../UI/VendorCard';
import { resolveImageSrc, getImageWithFallback } from '../../utils/images';

const { Title, Text, Paragraph } = Typography;

const SearchResults = ({ 
    type = 'vendors', // vendors, menu, all
    onItemClick,
    showPagination = true,
    showFilters = true
}) => {
    const { 
        searchResults, 
        loading, 
        pagination, 
        updatePagination,
        getFilterSummary
    } = useSearch();
    const { addToCart } = useCart();
    const { isInWishlist } = useWishlist();

    const [viewMode, setViewMode] = useState('grid'); // grid, list

    const results = type === 'all' ? searchResults : searchResults[type] || [];
    const total = type === 'all' ? searchResults.total : results.length;
    const activeFilters = getFilterSummary();

    // Handle pagination change
    const handlePaginationChange = (page, pageSize) => {
        updatePagination({ current: page, pageSize });
    };

    // Handle item click
    const handleItemClick = (item) => {
        onItemClick?.(item);
    };

    // Handle add to cart
    const handleAddToCart = (item) => {
        if (type === 'menu') {
            addToCart(item, 1);
        } else {
            // For vendors, redirect to vendor page
            window.location.href = `/vendors/${item.id}`;
        }
    };

    // Render vendor card
    const renderVendorCard = (vendor) => (
        <VendorCard
            key={`vendor_${vendor.id}`}
            vendor={vendor}
            onView={(v) => handleItemClick(v)}
            onViewMenu={(v) => handleItemClick(v)}
            extraTopRight={
                <Space size="small">
                    <WishlistButton item={vendor} type="vendor" size="small" />
                    <SocialShare item={vendor} type="vendor" size="small" />
                </Space>
            }
        />
    );

    // Render menu item card using reusable component
    const renderMenuItemCard = (item) => (
        <FoodCard
            key={`menu_${item.id}`}
            variant="menu"
            item={item}
            onView={(it) => handleItemClick(it)}
            onAddToCart={(it) => handleAddToCart(it)}
            onShare={() => {}}
            actionsPlacement="footer"
        />
    );

    // Render list view
    const renderListView = (items) => (
        <div className="space-y-3 sm:space-y-4" data-emoji-context={type === 'menu' ? 'snacks' : 'photography'}>
            {items.map((item) => (
                <Card key={item.id} className="card-ui modern-food-card hover:shadow-xl" size="small" style={{ borderRadius: '16px' }}>
                    <div className="flex gap-3 sm:gap-4">
                        <img
                            src={type === 'vendors' ? resolveImageSrc(item.logo || item.image_url || item.featured_image || item.url || item.file_path || '') : getImageWithFallback(item, 'medium')}
                            alt={type === 'vendors' ? item.business_name : item.name}
                            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl shadow-md"
                            onError={safeImageOnError}
                        />
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                                <Title level={4} className="mb-0">
                                    {type === 'vendors' ? item.business_name : item.name}
                                </Title>
                                <Space size="small">
                                    <WishlistButton 
                                        item={item} 
                                        type={type} 
                                        size="small"
                                    />
                                    <SocialShare 
                                        item={item} 
                                        type={type} 
                                        size="small"
                                    />
                                </Space>
                            </div>
                            <Paragraph ellipsis={{ rows: 2 }} className="mb-0">
                                {item.description}
                            </Paragraph>
                            <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center gap-4">
                                    {type === 'vendors' ? (
                                        <>
                                            <div className="flex items-center gap-1">
                                                <EnvironmentOutlined />
                                                <Text type="secondary">
                                                    {item.city?.name}
                                                </Text>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <StarOutlined className="text-yellow-500" />
                                                <Text>{item.rating}</Text>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <Text strong className="text-base text-green-600">
                                                {formatCurrency(item.price)}
                                            </Text>
                                            <Text type="secondary">
                                                {item.vendor?.business_name}
                                            </Text>
                                        </>
                                    )}
                                </div>
                                <div className="action-buttons-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto auto',
                                    gap: '8px',
                                    justifyItems: 'center',
                                    alignItems: 'end',
                                    justifyContent: 'center',
                                    alignContent: 'space-evenly'
                                }}>
                                    <Button 
                                        icon={<EyeOutlined />}
                                        onClick={() => handleItemClick(item)}
                                        size="small"
                                    >
                                        View
                                    </Button>
                                    <Button 
                                        type="primary"
                                        icon={<ShoppingCartOutlined />}
                                        onClick={() => handleItemClick(item)}
                                        size="small"
                                        className="btn-vibrant"
                                    >
                                        {type === 'vendors' ? 'View Menu' : 'Add to Cart'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            ))}
        </div>
    );

    // Render grid view
    const renderGridView = (items) => (
        <Row gutter={[12, 12]}>
            {items.map((item) => (
                <Col
                    key={item.id} 
                    xs={24} 
                    sm={12} 
                    md={8} 
                    lg={6}
                    xl={4}
                >
                    {type === 'vendors' ? renderVendorCard(item) : renderMenuItemCard(item)}
                </Col>
            ))}
        </Row>
    );

    // Render empty state
    const renderEmptyState = () => (
        <div className="text-center py-12">
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <div className="text-center">
                        <Title level={4}>No results found</Title>
                        <Paragraph type="secondary">
                            Try adjusting your search criteria or filters
                        </Paragraph>
                        {activeFilters.length > 0 && (
                            <div className="mt-4">
                                <Text type="secondary">Active filters:</Text>
                                <div className="mt-2">
                                    {activeFilters.map((filter, index) => (
                                        <Tag key={index} className="mb-1">
                                            {filter}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                }
            />
        </div>
    );

    // Render loading state
    if (loading) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <Title level={4} className="mb-0">Search Results</Title>
                    <Spin />
                </div>
                <Row gutter={[16, 16]}>
                    {Array.from({ length: 8 }).map((_, index) => (
                        <Col key={index} xs={24} sm={12} md={8} lg={6}>
                            <LoadingSkeleton.VendorCardSkeleton />
                        </Col>
                    ))}
                </Row>
            </div>
        );
    }

    // Render no results
    if (!results || results.length === 0) {
        return renderEmptyState();
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <Title level={4} className="mb-0">
                        Search Results ({total})
                    </Title>
                    {activeFilters.length > 0 && (
                        <div className="mt-1">
                            <Text type="secondary">
                                Filtered by: {activeFilters.join(', ')}
                            </Text>
                        </div>
                    )}
                </div>
                
                <Space size="small">
                    <Button.Group>
                        <Button 
                            type={viewMode === 'grid' ? 'primary' : 'default'}
                            onClick={() => setViewMode('grid')}
                        >
                            Grid
                        </Button>
                        <Button 
                            type={viewMode === 'list' ? 'primary' : 'default'}
                            onClick={() => setViewMode('list')}
                        >
                            List
                        </Button>
                    </Button.Group>
                </Space>
            </div>

            <Divider className="my-2" />

            {/* Results */}
            {viewMode === 'grid' ? renderGridView(results) : renderListView(results)}

            {/* Pagination */}
            {showPagination && total > pagination.pageSize && (
                <div className="flex justify-center mt-6">
                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={total}
                        onChange={handlePaginationChange}
                        showSizeChanger
                        showQuickJumper
                        showTotal={(total, range) => 
                            `${range[0]}-${range[1]} of ${total} items`
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default SearchResults;
